package com.tfc.care.tfccare.ui.theme

import androidx.compose.foundation.isSystemInDarkTheme
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Typography
import androidx.compose.material3.darkColorScheme
import androidx.compose.material3.lightColorScheme
import androidx.compose.runtime.Composable
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.TextStyle
import androidx.compose.ui.text.font.FontFamily
import androidx.compose.ui.text.font.FontWeight
import org.jetbrains.compose.resources.Font
import tfccare.composeapp.generated.resources.Res
import tfccare.composeapp.generated.resources.devnagri


// Light Mode Colors
val primaryLight = Color(0xFF15803D) // Green-700
val onPrimaryLight = Color(0xFFFFFFFF)
val primaryContainerLight = Color(0xFFBBF7D0) // Green-200
val onPrimaryContainerLight = Color(0xFF052E16) // Green-950

val secondaryLight = Color(0xFF14532D) // Green-900
val onSecondaryLight = Color(0xFFFFFFFF)
val secondaryContainerLight = Color(0xFFD1FAE5) // Green-100
val onSecondaryContainerLight = Color(0xFF052E16)

val tertiaryLight = Color(0xFF0D9488) // Teal-600
val onTertiaryLight = Color(0xFFFFFFFF)
val tertiaryContainerLight = Color(0xFFCCFBF1) // Teal-100
val onTertiaryContainerLight = Color(0xFF134E4A) // Teal-900

val errorLight = Color(0xFFDC2626) // Red-600
val onErrorLight = Color(0xFFFFFFFF)
val errorContainerLight = Color(0xFFFEE2E2) // Red-100
val onErrorContainerLight = Color(0xFF7F1D1D) // Red-900

val backgroundLight = Color(0xFFFAFAFA) // Neutral-50
val onBackgroundLight = Color(0xFF171717) // Neutral-900

val surfaceLight = Color(0xFFFFFFFF)
val onSurfaceLight = Color(0xFF171717)

val surfaceVariantLight = Color(0xFFF5F5F5) // Neutral-100
val onSurfaceVariantLight = Color(0xFF525252) // Neutral-600

val outlineLight = Color(0xFFD4D4D4) // Neutral-300
val outlineVariantLight = Color(0xFFE5E5E5) // Neutral-200

val scrimLight = Color(0xFF000000)

val inverseSurfaceLight = Color(0xFF262626) // Neutral-800
val inverseOnSurfaceLight = Color(0xFFFAFAFA)
val inversePrimaryLight = Color(0xFF4ADE80) // Green-400

val surfaceDimLight = Color(0xFFF5F5F5)
val surfaceBrightLight = Color(0xFFFFFFFF)
val surfaceContainerLowestLight = Color(0xFFFFFFFF)
val surfaceContainerLowLight = Color(0xFFFAFAFA)
val surfaceContainerLight = Color(0xFFF5F5F5)
val surfaceContainerHighLight = Color(0xFFE5E5E5)
val surfaceContainerHighestLight = Color(0xFFD4D4D4)




// Dark Mode Colors
val primaryDark = Color(0xFF22C55E) // Green-500
val onPrimaryDark = Color(0xFF052E16) // Green-950
val primaryContainerDark = Color(0xFF166534) // Green-800
val onPrimaryContainerDark = Color(0xFFBBF7D0) // Green-200

val secondaryDark = Color(0xFF86EFAC) // Green-300
val onSecondaryDark = Color(0xFF052E16)
val secondaryContainerDark = Color(0xFF14532D) // Green-900
val onSecondaryContainerDark = Color(0xFFD1FAE5) // Green-100

val tertiaryDark = Color(0xFF5EEAD4) // Teal-300
val onTertiaryDark = Color(0xFF134E4A) // Teal-900
val tertiaryContainerDark = Color(0xFF0F766E) // Teal-700
val onTertiaryContainerDark = Color(0xFFCCFBF1) // Teal-100

val errorDark = Color(0xFFFCA5A5) // Red-300
val onErrorDark = Color(0xFF7F1D1D) // Red-900
val errorContainerDark = Color(0xFFDC2626) // Red-600
val onErrorContainerDark = Color(0xFFFEE2E2) // Red-100

val backgroundDark = Color(0xFF0C130C)
val onBackgroundDark = Color(0xFFF5F5F5)

val surfaceDark = Color(0xFF15181F)
val onSurfaceDark = Color(0xFFF5F5F5)

val surfaceVariantDark = Color(0xFF1E242E) // Neutral-900  #1E242E
val onSurfaceVariantDark = Color(0xFFA3A3A3) // Neutral-400

val outlineDark = Color(0xFF525252) // Neutral-600
val outlineVariantDark = Color(0xFF262626) // Neutral-800

val scrimDark = Color(0xFF000000)

val inverseSurfaceDark = Color(0xFFF5F5F5)
val inverseOnSurfaceDark = Color(0xFF171717)
val inversePrimaryDark = Color(0xFF15803D) // Green-700

val surfaceDimDark = Color(0xFF0A0A0A)
val surfaceBrightDark = Color(0xFF262626)
val surfaceContainerLowestDark = Color(0xFF000000)
val surfaceContainerLowDark = Color(0xFF0F0F0F)
val surfaceContainerDark = Color(0xFF171717)
val surfaceContainerHighDark = Color(0xFF1F1F1F)
val surfaceContainerHighestDark = Color(0xFF292929)


private val LightColorScheme = lightColorScheme(
    primary = primaryLight,
    onPrimary = onPrimaryLight,
    primaryContainer = primaryContainerLight,
    onPrimaryContainer = onPrimaryContainerLight,
    secondary = secondaryLight,
    onSecondary = onSecondaryLight,
    secondaryContainer = secondaryContainerLight,
    onSecondaryContainer = onSecondaryContainerLight,
    tertiary = tertiaryLight,
    onTertiary = onTertiaryLight,
    tertiaryContainer = tertiaryContainerLight,
    onTertiaryContainer = onTertiaryContainerLight,
    error = errorLight,
    onError = onErrorLight,
    errorContainer = errorContainerLight,
    onErrorContainer = onErrorContainerLight,
    background = backgroundLight,
    onBackground = onBackgroundLight,
    surface = surfaceLight,
    onSurface = onSurfaceLight,
    surfaceVariant = surfaceVariantLight,
    onSurfaceVariant = onSurfaceVariantLight,
    outline = outlineLight,
    outlineVariant = outlineVariantLight,
    scrim = scrimLight,
    inverseSurface = inverseSurfaceLight,
    inverseOnSurface = inverseOnSurfaceLight,
    inversePrimary = inversePrimaryLight,
    surfaceDim = surfaceDimLight,
    surfaceBright = surfaceBrightLight,
    surfaceContainerLowest = surfaceContainerLowestLight,
    surfaceContainerLow = surfaceContainerLowLight,
    surfaceContainer = surfaceContainerLight,
    surfaceContainerHigh = surfaceContainerHighLight,
    surfaceContainerHighest = surfaceContainerHighestLight,
)

private val DarkColorScheme = darkColorScheme(
    primary = primaryDark,
    onPrimary = onPrimaryDark,
    primaryContainer = primaryContainerDark,
    onPrimaryContainer = onPrimaryContainerDark,
    secondary = secondaryDark,
    onSecondary = onSecondaryDark,
    secondaryContainer = secondaryContainerDark,
    onSecondaryContainer = onSecondaryContainerDark,
    tertiary = tertiaryDark,
    onTertiary = onTertiaryDark,
    tertiaryContainer = tertiaryContainerDark,
    onTertiaryContainer = onTertiaryContainerDark,
    error = errorDark,
    onError = onErrorDark,
    errorContainer = errorContainerDark,
    onErrorContainer = onErrorContainerDark,
    background = backgroundDark,
    onBackground = onBackgroundDark,
    surface = surfaceDark,
    onSurface = onSurfaceDark,
    surfaceVariant = surfaceVariantDark,
    onSurfaceVariant = onSurfaceVariantDark,
    outline = outlineDark,
    outlineVariant = outlineVariantDark,
    scrim = scrimDark,
    inverseSurface = inverseSurfaceDark,
    inverseOnSurface = inverseOnSurfaceDark,
    inversePrimary = inversePrimaryDark,
    surfaceDim = surfaceDimDark,
    surfaceBright = surfaceBrightDark,
    surfaceContainerLowest = surfaceContainerLowestDark,
    surfaceContainerLow = surfaceContainerLowDark,
    surfaceContainer = surfaceContainerDark,
    surfaceContainerHigh = surfaceContainerHighDark,
    surfaceContainerHighest = surfaceContainerHighestDark,
)


@Composable
fun AppTheme(
    darkTheme: Boolean = isSystemInDarkTheme(),
    content: @Composable () -> Unit
) {
    val colorScheme = if (darkTheme) DarkColorScheme else LightColorScheme


    val notoDevanagari = FontFamily(
        Font(
            Res.font.devnagri,
            weight = FontWeight.Normal
        )
    )

    val appTypography = Typography(
        bodyLarge = TextStyle(fontFamily = notoDevanagari),
        bodyMedium = TextStyle(fontFamily = notoDevanagari),
        bodySmall = TextStyle(fontFamily = notoDevanagari),
        titleLarge = TextStyle(fontFamily = notoDevanagari),
        titleMedium = TextStyle(fontFamily = notoDevanagari),
        // ... add others as needed
    )

    MaterialTheme(
        colorScheme = colorScheme,
        content = content,
        typography =appTypography
    )
}
